import { Box, CircularProgress, styled, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, NavLink } from "react-router-dom";
import { forgotPasswordSchema } from "@/schemas/auth.schema";
import { forgotPassword } from "@/services/auth.service";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { CustomButton } from "@/components/custom/custom-button";
import Logo from "assets/images/newLogo.png";
import { useDispatch } from "react-redux";
import { showToast } from "@/redux/actions/toast-actions";

const PageRoot = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(3),
}));

const Card = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 440,
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  padding: theme.spacing(4),
  border: `1px solid ${theme.palette.divider}`,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  marginBottom: theme.spacing(1),
}));

const ForgotPassword = () => {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: ({ email }) => forgotPassword({ email }),
    onSuccess: (data, variables) => {
      dispatch(showToast(data?.message ?? "Email sent", "success"));
      navigate("/session/reset-password", {
        state: { email: variables.email },
      });
    },
  });
  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: forgotPasswordSchema,
    onSubmit: (values) => {
      mutation.mutate(values);
    },
  });

  return (
    <PageRoot>
      <Card>
        <Header>
          <NavLink to="/">
            <img src={Logo} alt="logo" style={{ width: 120 }} />
          </NavLink>
        </Header>

        <Box>
          <Typography variant="h5" fontWeight={700} mb={0.5}>
            Reset Your Password
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter your email address and we'll send you a reset link.
          </Typography>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <FormTextInput
              formik={formik}
              name="email"
              label="Email Address"
              type="email"
              isDisabled={mutation.isPending}
            />

            {mutation.isError && (
              <Typography variant="body2" color="error">
                {mutation.error?.message ?? "Something went wrong. Try again."}
              </Typography>
            )}

            <CustomButton
              type="submit"
              disabled={!formik.isValid || !formik.dirty || mutation.isPending}
              sx={{ height: 44, mt: 1 }}
            >
              {mutation.isPending ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress
                    size={16}
                    thickness={5}
                    sx={{ color: "#fff" }}
                  />
                  Sending...
                </Box>
              ) : (
                "Request Password Reset"
              )}
            </CustomButton>

            <Typography
              variant="body2"
              textAlign="center"
              color="text.secondary"
            >
              Remember your password?{" "}
              <NavLink
                to="/session/login"
                style={{ color: "inherit", fontWeight: 600 }}
              >
                Sign in
              </NavLink>
            </Typography>
          </Box>
        </form>
      </Card>
    </PageRoot>
  );
};

export default ForgotPassword;
